/**
 * ServiceTimesManager — the weekly rhythm of one assembly.
 *
 * Set the pattern once: the calendar projects it forward and every public one
 * appears on the assembly's page and on its province page. Shared by the
 * church module's Services tab and the ecosystem calendar dialog.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Eye, EyeOff, Loader2 } from 'lucide-react';

const BLANK = {
  name: '', weekday: 6, start_time: '09:00', duration_mins: 90,
  location: '', description: '', visibility: 'public', is_active: true,
};

export const ServiceTimesManager = ({ churchId }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(BLANK);

  const { data, isLoading } = useQuery({
    queryKey: ['service-patterns', churchId],
    queryFn: async () => (await api.get(`/church/${churchId}/service-patterns`)).data,
    enabled: !!churchId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['service-patterns', churchId] });
    qc.invalidateQueries({ queryKey: ['grouping-overview'] });
  };

  const add = useMutation({
    mutationFn: () => api.post(`/church/${churchId}/service-patterns`, {
      ...form, weekday: Number(form.weekday), duration_mins: Number(form.duration_mins) }),
    onSuccess: () => { toast.success('Weekly service added'); setForm(BLANK); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setVisibility = useMutation({
    mutationFn: ({ pattern, visibility }) => api.put(
      `/church/${churchId}/service-patterns/${pattern.id}`,
      { name: pattern.name, weekday: pattern.weekday, start_time: pattern.start_time,
        duration_mins: pattern.duration_mins, location: pattern.location,
        description: pattern.description, visibility, is_active: pattern.is_active }),
    onSuccess: (_r, v) => {
      toast.success(v.visibility === 'public' ? 'Now shown publicly' : 'Hidden from the public page');
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/church/${churchId}/service-patterns/${id}`),
    onSuccess: () => { toast.success('Removed'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const items = data?.items || [];

  return (
    <div className="space-y-3" data-testid="service-times-manager">
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      ) : items.length === 0 ? (
        <p className="rounded-lg bg-slate-50 py-6 text-center text-xs text-slate-500"
           data-testid="patterns-empty">
          No weekly pattern yet. Add "Sunday 09:00 Main Service" below and it repeats forever —
          and shows on your province page.
        </p>
      ) : items.map((p) => (
        <div key={p.id}
             className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2"
             data-testid={`pattern-row-${p.id}`}>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
            <p className="text-[11px] text-slate-500">
              {p.weekday_label} {p.start_time} · {p.duration_mins} mins
              {p.location ? ` · ${p.location}` : ''}
              {p.visibility === 'public' ? ' · public' : ' · internal'}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                    onClick={() => setVisibility.mutate({
                      pattern: p,
                      visibility: p.visibility === 'public' ? 'internal' : 'public' })}
                    data-testid={`pattern-visibility-${p.id}`}>
              {p.visibility === 'public'
                ? <><Eye className="h-3.5 w-3.5 mr-1" /> Public</>
                : <><EyeOff className="h-3.5 w-3.5 mr-1" /> Internal</>}
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600"
                    onClick={() => remove.mutate(p.id)} data-testid={`pattern-delete-${p.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}

      <div className="space-y-3 rounded-lg border border-dashed border-slate-300 p-3">
        <div><Label>Service name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 placeholder="Main Service" data-testid="pattern-name-input" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div><Label>Day</Label>
            <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: e.target.value })}
                    data-testid="pattern-weekday-select"
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
              {(data?.weekdays || []).map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <div><Label>Starts</Label>
            <Input type="time" value={form.start_time} data-testid="pattern-time-input"
                   onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
          </div>
          <div><Label>Minutes</Label>
            <Input type="number" value={form.duration_mins} data-testid="pattern-duration-input"
                   onChange={(e) => setForm({ ...form, duration_mins: e.target.value })} />
          </div>
        </div>
        <div><Label>Location</Label>
          <Input value={form.location} data-testid="pattern-location-input"
                 onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </div>
        <label className="flex cursor-pointer items-center gap-2">
          <input type="checkbox" checked={form.visibility === 'public'} data-testid="pattern-public-checkbox"
                 onChange={(e) => setForm({ ...form, visibility: e.target.checked ? 'public' : 'internal' })} />
          <span className="text-xs text-slate-600">Show on the public church and province pages</span>
        </label>
        <Button size="sm" onClick={() => add.mutate()} disabled={add.isPending || !form.name.trim()}
                data-testid="pattern-add-btn">
          {add.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                         : <Plus className="mr-1 h-3.5 w-3.5" />}
          Add weekly service
        </Button>
      </div>
    </div>
  );
};

export default ServiceTimesManager;
